import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { AIRPORTS, FBOS, SITE, SERVICES } from "@/lib/site-data";
import { CTABand } from "@/components/CTA";
import { CrossLinkModule } from "@/components/CrossLinks";
import { NetworkSuggestion } from "@/components/OurNetwork";
import { Plane, Building2, Phone, MapPin, Users, Sparkles, Clock } from "lucide-react";
import { breadcrumbJsonLd } from "@/lib/breadcrumbs";
import { ldJson, hoursSpec24x7 } from "@/lib/seo-schema";
import { airportFaqList, airportFaqSchema } from "@/lib/airport-faq";
import { AIRPORT_TERMINALS } from "@/lib/airport-terminals";
import { AirportLocalBlock } from "@/components/AirportLocalBlock";
import { HourlyRates } from "@/components/HourlyRates";

import { NearbyCities } from "@/components/NearbyCities";
import { nearbyCities, nearbyCitiesItemList } from "@/lib/airport-nearby";
import { AnswerBlock } from "@/components/AnswerBlock";
import { airportAnswer } from "@/lib/answer-first";
import { StaticPicture } from "@/components/StaticPicture";
import {
  airportHeroPath,
  airportHeroUrl,
  airportHeroAlt,
  airportImageSchema,
  meetGreetAlt,
  meetGreetDescription,
  MEET_GREET_IMAGE_PATH,
  AIRPORT_IMAGE_WIDTH,
  AIRPORT_IMAGE_HEIGHT,
} from "@/lib/airport-images";
import { AirportGallery } from "@/components/AirportGallery";
import {
  airportGallerySchema,
  airportGalleryImageRefs,
  airportGalleryCollectionSchema,
} from "@/lib/airport-gallery";


export const Route = createFileRoute("/airports/$code")({
  loader: ({ params }) => {
    const airport = AIRPORTS.find((a) => a.slug === params.code);
    if (!airport) throw notFound();
    return { airport };
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "Airport not found" }, { name: "robots", content: "noindex" }] };
    }
    const a = loaderData.airport;
    const shortTitle = `${a.code} Airport Chauffeur Service`;
    const title = `${shortTitle} | Premier Executive`;
    const desc = `Flight-tracked chauffeur service at ${a.name} (${a.code}): ${a.code === "SJC" ? "baggage-claim greeting" : "curbside or meet-and-greet arrivals"}, flat rates, ${a.city} pickups — reserve online anytime.`.slice(0, 160);
    const url = `https://premierexecutivechauffeur.com/airports/${a.slug}`;
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { property: "og:image", content: airportHeroUrl(a.slug) },
        { property: "og:image:width", content: String(AIRPORT_IMAGE_WIDTH) },
        { property: "og:image:height", content: String(AIRPORT_IMAGE_HEIGHT) },
        { property: "og:image:alt", content: airportHeroAlt(a) },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: desc },
        { name: "twitter:image", content: airportHeroUrl(a.slug) },
        { name: "twitter:image:alt", content: airportHeroAlt(a) },
      ],
      links: [
        { rel: "canonical", href: url },
        { rel: "preload", as: "image", href: airportHeroPath(a.slug), fetchPriority: "high" },
      ],
      scripts: [
        breadcrumbJsonLd([
          { name: "Home", path: "/" },
          { name: "Airports & FBOs", path: "/airports" },
          { name: `${a.code} — ${a.name}`, path: url },
        ]),
        {
          type: "application/ld+json",
          children: ldJson({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "@id": `${url}#webpage`,
            url,
            name: shortTitle,
            description: desc,
            abstract: airportAnswer({ code: a.code, name: a.name, city: a.city }).answer,
            inLanguage: "en-US",
            isPartOf: { "@id": "https://premierexecutivechauffeur.com#website" },
            about: { "@id": `${url}#airport` },
            primaryImageOfPage: { "@id": `${url}#hero-image` },
            image: [
              { "@id": `${url}#hero-image` },
              { "@id": `${url}#meet-greet-image` },
              ...airportGalleryImageRefs(url),
            ],
            hasPart: { "@id": `${url}#gallery` },
            breadcrumb: { "@id": `${url}#breadcrumb` },
            speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", ".answer-first"] },
          }),
        },
        {
          type: "application/ld+json",
          children: ldJson({
            "@context": "https://schema.org",
            "@type": "Airport",
            "@id": `${url}#airport`,
            name: a.name,
            iataCode: a.code,
            address: { "@type": "PostalAddress", addressLocality: a.city, addressRegion: "CA", addressCountry: "US" },
            url,
          }),
        },
        {
          type: "application/ld+json",
          children: ldJson({
            "@context": "https://schema.org",
            "@type": "Service",
            "@id": `${url}#service`,
            name: `${a.code} Airport Chauffeur Service`,
            description: desc,
            serviceType: "Airport Transfer",
            url,
            provider: { "@id": "https://premierexecutivechauffeur.com#business" },
            areaServed: { "@type": "Airport", name: a.name, iataCode: a.code },
            hoursAvailable: hoursSpec24x7,
          }),
        },
        { type: "application/ld+json", children: ldJson(airportFaqSchema(a, url)) },
        ...airportImageSchema(a, url).map((node) => ({
          type: "application/ld+json",
          children: ldJson(node),
        })),
        ...airportGallerySchema(a, url).map((node) => ({
          type: "application/ld+json",
          children: ldJson(node),
        })),
        {
          type: "application/ld+json",
          children: ldJson(airportGalleryCollectionSchema(a, url)),
        },
        {
          type: "application/ld+json",
          children: ldJson(nearbyCitiesItemList(a.code, url, nearbyCities(a.slug, 12))),
        },
        // NOTE: no extra speakableJsonLd() node here — the #webpage node above
        // already declares speakable (["h1", ".answer-first"]). A second WebPage
        // for the same URL with a different selector set confuses answer engines.

      ],
    };
  },

  notFoundComponent: () => (
    <section className="container-x py-24 max-w-2xl">
      <h1 className="text-3xl mb-3">Airport not found</h1>
      <p className="text-muted-foreground mb-6">We don't have a dedicated page for that airport yet.</p>
      <Link to="/airports" className="btn-outline">All airports</Link>
    </section>
  ),
  errorComponent: () => (
    <section className="container-x py-24 max-w-2xl">
      <h1 className="text-3xl mb-3">Something went wrong</h1>
      <Link to="/airports" className="btn-outline">Back to airports</Link>
    </section>
  ),
  component: AirportDetail,
});

function AirportDetail() {
  const { airport: a } = Route.useLoaderData();
  const relatedFbos = FBOS.filter((f) => f.airport === a.code);
  const nearby = nearbyCities(a.slug, 12);
  const faqs = airportFaqList(a);
  const relatedServices = SERVICES.filter((s) =>
    ["airport-transfers", "corporate", "private-aviation", "sprinter-groups"].includes(s.slug),
  );

  return (
    <>
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <StaticPicture
            src={airportHeroPath(a.slug)}
            alt={airportHeroAlt(a)}
            width={AIRPORT_IMAGE_WIDTH}
            height={AIRPORT_IMAGE_HEIGHT}
            loading="eager"
            fetchPriority="high"
            sizes="100vw"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/90 to-background/60" />
        </div>
        <div className="relative container-x py-20 md:py-28 max-w-4xl">
        <nav aria-label="Breadcrumb" className="eyebrow mb-4">
          <Link to="/" className="hover:text-gold-soft">Home</Link>
          <span className="mx-2">/</span>
          <Link to="/airports" className="hover:text-gold-soft">Airports & FBOs</Link>
          <span className="mx-2">/</span>
          <span>{a.code}</span>
        </nav>
        <div className="flex items-baseline gap-4 mb-4">
          <span className="font-display text-5xl text-gold">{a.code}</span>
          <Plane className="h-6 w-6 text-gold" />
        </div>
        <h1 className="text-4xl md:text-5xl mb-6">{a.name} ({a.code}) Chauffeur Service</h1>
        <p className="eyebrow mb-4 text-gold-soft">{a.city}, California · Flight-tracked · CPUC-licensed · staffed dispatch</p>
        <AnswerBlock
          className="mb-8"
          content={airportAnswer({ code: a.code, name: a.name, city: a.city })}
        />
        <p className="text-lg text-muted-foreground mb-4 speakable">{a.blurb}</p>
        <p className="text-muted-foreground speakable">{a.long}</p>
        <div className="flex flex-wrap gap-3 mt-8">
          <a href={SITE.bookUrl} target="_blank" rel="noopener noreferrer" className="btn-gold hover:btn-gold-hover">Reserve {a.code} chauffeur</a>
          <a href={SITE.phoneHref} className="btn-outline hover:bg-gold hover:text-primary-foreground inline-flex items-center gap-2"><Phone className="h-4 w-4" />Call {SITE.phone}</a>
        </div>
        </div>
      </section>

      <section className="container-x py-14 max-w-4xl" aria-label={`${a.code} meet-and-greet photo`}>
        <figure className="border border-border overflow-hidden bg-surface">
          <StaticPicture
            src={MEET_GREET_IMAGE_PATH}
            alt={meetGreetAlt(a)}
            width={AIRPORT_IMAGE_WIDTH}
            height={AIRPORT_IMAGE_HEIGHT}
            sizes="(min-width: 1024px) 896px, 100vw"
            className="w-full h-auto block"
          />
          <figcaption className="p-4 text-sm text-muted-foreground">{meetGreetDescription(a)}</figcaption>
        </figure>
      </section>

      <AirportGallery airport={a} />



      {AIRPORT_TERMINALS[a.slug] && (() => {
        const t = AIRPORT_TERMINALS[a.slug];
        return (
          <section className="border-t border-border bg-surface" aria-labelledby="airport-terminals-heading">
            <div className="container-x py-16 max-w-4xl">
              <p className="eyebrow mb-2">Terminals &amp; pickup zones</p>
              <h2 id="airport-terminals-heading" className="text-2xl mb-6">Where to meet your chauffeur at {a.code}</h2>
              <div className="grid gap-4 md:grid-cols-2 mb-10">
                {t.terminals.map((term) => (
                  <div key={term.name} className="border border-border bg-background p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <MapPin className="h-5 w-5 text-gold" aria-hidden />
                      <p className="text-lg text-foreground">{term.name}</p>
                    </div>
                    <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">{term.airlines}</p>
                    <p className="text-sm text-muted-foreground">{term.note}</p>
                  </div>
                ))}
              </div>

              <div className="grid gap-6 md:grid-cols-2">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Plane className="h-5 w-5 text-gold" aria-hidden />
                    <p className="text-lg text-foreground">Domestic arrivals</p>
                  </div>
                  <p className="text-sm text-muted-foreground">{t.domesticPickup}</p>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Plane className="h-5 w-5 text-gold" aria-hidden />
                    <p className="text-lg text-foreground">International arrivals</p>
                  </div>
                  <p className="text-sm text-muted-foreground">{t.internationalPickup}</p>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="h-5 w-5 text-gold" aria-hidden />
                    <p className="text-lg text-foreground">Meet-and-greet</p>
                  </div>
                  <p className="text-sm text-muted-foreground">{t.meetAndGreet}</p>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="h-5 w-5 text-gold" aria-hidden />
                    <p className="text-lg text-foreground">Private aviation</p>
                  </div>
                  <p className="text-sm text-muted-foreground">{t.privateAviation}</p>
                </div>
              </div>

              <div className="mt-10 border-l-2 border-gold pl-4">
                <p className="text-sm text-foreground mb-2">Nearby destinations</p>
                <p className="text-sm text-muted-foreground mb-4">{t.nearbyDestinations}</p>
                <div className="flex items-start gap-2">
                  <Clock className="h-4 w-4 text-gold mt-0.5" aria-hidden />
                  <p className="text-xs text-muted-foreground">{t.peakTiming}</p>
                </div>
              </div>
            </div>
          </section>
        );
      })()}

      <AirportLocalBlock airport={a} />

      {/* Operating standards are documented once on /services — linked, not duplicated. */}


      <section className="border-t border-border">


        <div className="container-x py-14 max-w-4xl">
          <h2 className="text-2xl mb-6">Services at {a.code}</h2>
          <div className="grid gap-3 md:grid-cols-2">
            {relatedServices.map((s) => (
              <div key={s.slug} className="border border-border p-5">
                <Link
                  to="/services/$service"
                  params={{ service: s.slug }}
                  className="text-foreground hover:text-gold transition-colors"
                >
                  {s.title}
                  <span className="sr-only"> at {a.code}</span>
                </Link>
                <p className="text-xs text-muted-foreground mt-1">{s.short}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {relatedFbos.length > 0 && (
        <section className="border-t border-border bg-surface">
          <div className="container-x py-16">
            <h2 className="text-2xl mb-6">FBOs at {a.code}</h2>
            <div className="grid gap-3 md:grid-cols-2">
              {relatedFbos.map((f) => (
                <div
                  key={f.slug}
                  className="border border-border bg-background px-5 py-4 flex items-start gap-3"
                >
                  <Building2 className="h-5 w-5 text-gold mt-0.5" />
                  <div>
                    <Link
                      to="/fbos/$slug"
                      params={{ slug: f.slug }}
                      className="text-foreground hover:text-gold-soft transition-colors"
                    >
                      {f.name}
                    </Link>
                    <p className="text-xs text-muted-foreground mt-1">{f.blurb}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <NearbyCities code={a.code} airportName={a.name} items={nearby} />


      <section className="border-t border-border bg-surface">
        <div className="container-x py-16 max-w-4xl">
          <h2 className="text-2xl mb-4">Other Bay Area airports</h2>
          <div className="grid gap-3 md:grid-cols-3">
            {AIRPORTS.filter((x) => x.slug !== a.slug).map((x) => (
              <Link
                key={x.slug}
                to="/airports/$code"
                params={{ code: x.slug }}
                className="border border-border bg-background px-5 py-4 hover:border-gold transition-colors"
              >
                <div className="flex items-baseline gap-2">
                  <span className="font-display text-lg text-gold">{x.code}</span>
                  <span className="text-sm">{x.name}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <HourlyRates
        showFaqs={false}
        heading={`${a.code} chauffeur rates`}
        intro={`Airport transfers to and from ${a.name} (${a.code}) are quoted as flat rates. For multi-stop days, roadshows, and crew movements, published hourly as-directed pricing applies with a five-hour minimum. Tolls, parking, ${a.code} access fees, and gratuity treatment are shown in writing; gratuity is excluded unless confirmed otherwise.`}
      />

      <section id="faq" className="border-t border-border">
        <div className="container-x py-16 max-w-3xl speakable">

          <p className="eyebrow mb-2">Hours & timing</p>
          <h2 className="text-2xl mb-6">{a.code} chauffeur service — frequently asked questions</h2>
          <p className="text-sm text-muted-foreground mb-6">
            Dispatch operates every day, including holidays, with late-night and red-eye arrivals pre-arranged. We stage vehicles at {a.code} 15 minutes before wheels-down on tracked flights.
          </p>
          <div className="space-y-3">
            {faqs.map((f) => (
              <details key={f.q} className="border border-border p-5 group">
                <summary className="cursor-pointer text-foreground group-open:text-gold">{f.q}</summary>
                <p className="mt-3 text-sm text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="container-x py-8">
        <NetworkSuggestion context={{ type: "airport", slug: a.slug }} />
      </section>

      <CrossLinkModule context={{ type: "airport", slug: a.slug }} showFaqs={false} />

      <CTABand heading={`Reserve your ${a.code} chauffeur`} sub={`Flight-tracked, pre-arranged, and dispatched daily. Call ${SITE.phone}.`} />
    </>
  );
}
